import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useRef } from "react";

export default function EditorialSpotlight() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [30, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [10, -50]);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-canvas relative" data-testid="editorial-spotlight">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left image, spotlight clip */}
        <motion.figure style={{ y: y1 }} className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] grain">
            <img
              src="https://images.unsplash.com/photo-1687422808424-4a8e78fa6ae8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxibGFjayUyMHdvbWFuJTIwc21pbGluZyUyMHNtYXJ0cGhvbmV8ZW58MHx8fHwxNzg1NjA2NjE1fDA&ixlib=rb-4.1.0&q=85"
              alt="Recipient smiling as a transfer lands"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/60 via-transparent to-transparent" />
            {/* Spotlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 30% 35%, transparent 24%, rgba(4,26,16,0.55) 70%)",
              }}
            />
            <div className="absolute left-6 top-6 flex items-center gap-2">
              <span className="live-dot" />
              <span className="text-canvas/90 text-[10px] tracking-[0.24em] uppercase">
                Nairobi · 09:41 EAT
              </span>
            </div>
            <figcaption className="absolute left-6 right-6 bottom-6 text-canvas">
              <div className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                &ldquo;It came through before I finished the voice note.&rdquo;
              </div>
              <div className="mt-2 text-canvas/70 text-sm">— Wanjiku, receiving from London</div>
            </figcaption>
          </div>
        </motion.figure>

        {/* Right: pull-quote + secondary image */}
        <div className="lg:col-span-7">
          <div className="label-eyebrow">{t("home.editorial.eyebrow")}</div>
          <blockquote className="mt-6 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.02] tracking-tightest">
            Remittance is love with a{" "}
            <span className="italic font-serif text-forest/85">timestamp.</span> We built Instantxfer CashApp so the
            timestamp never gets in the way.
          </blockquote>

          <div className="mt-16 grid grid-cols-12 gap-6">
            <motion.figure style={{ y: y2 }} className="col-span-8 relative rounded-3xl overflow-hidden aspect-[6/5]">
              <img
                src="https://images.unsplash.com/photo-1608286022625-bc07f7a21154?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwbW9iaWxlJTIwcGF5bWVudHxlbnwwfHx8fDE3ODMxMzc1NjB8MA&ixlib=rb-4.1.0&q=85"
                alt="Person scanning a QR to pay at a merchant"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-forest/25 mix-blend-multiply" />
              <div className="absolute left-5 bottom-5 text-canvas">
                <div className="text-[10px] tracking-[0.24em] uppercase text-canvas/70">Scan · Kigali</div>
                <div className="mt-1 font-display text-lg">@grace.instant · 4,200 RWF</div>
              </div>
            </motion.figure>

            <div className="col-span-4 card-flat p-6 flex flex-col justify-between">
              <div>
                <div className="label-eyebrow">Since sunrise</div>
                <div className="mt-3 font-display text-4xl text-forest tracking-tightest">
                  1,284,910
                </div>
                <div className="text-ink/60 text-sm">transfers processed today</div>
              </div>
              <div className="mt-6">
                <div className="label-eyebrow">Median time</div>
                <div className="mt-2 font-display text-3xl text-forest tracking-tightest">
                  1m 42s
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
